import {
	Box,
	Button,
	Card,
	CardContent,
	Divider,
	Grid,
	IconButton,
	Stack,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { Link, useNavigate, useParams } from 'react-router-dom';
import KeyboardArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardArrowLeftOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FormatLineSpacingOutlinedIcon from '@mui/icons-material/FormatLineSpacingOutlined';
import { useCallback, useState } from 'react';
import { useGetPreAdmissionDetailQuery } from '@/api/pre-admission/preAdmission';
import {
	FormManagementStatus,
	FormManagementType,
	FormManagementTypeEnum,
	PermissionEnum,
	PreAdmissionTab,
} from '@/api/enum/common.enum';
import FormListContainer from './FormListContainer';
import { useGetFaceSheetListQuery } from '@/api/forms-management/faceSheet';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import UploadFormDialog from './UploadFormDialog';
import {
	useGetFamilyDetailQuery,
	useGetFormsFamilyListQuery,
} from '@/api/family-management/familyApi';
import { useGetUser } from '@/hooks/useGetUser';

export default function FamilyFormsManagementPage() {
	const [uploadType, setUploadType] = useState<FormManagementType | null>(null);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { familyId } = useParams();
	const [expanded, setExpanded] = useState<boolean>(false);
	const showModal = useModal();
	const { userInfo } = useGetUser();
	const [familyMemberExpanded, setFamilyMemberExpanded] =
		useState<boolean>(true);

	const { data: details } = useGetFamilyDetailQuery(familyId || '', {
		refetchOnMountOrArgChange: true,
	});

	const { data: faceSheetList }: any = useGetFormsFamilyListQuery(
		familyId || '',
		{ refetchOnMountOrArgChange: true }
	);

	const uploadButton = useCallback(
		(type: FormManagementType) =>
			userInfo?.permissionList?.includes(PermissionEnum.UploadOnlineForm) && (
				<Button
					key={`upload-btn-${type}`}
					onClick={(e) => {
						e.stopPropagation();
						handleOpenUpload(type);
					}}
					sx={{ marginRight: '8px', textTransform: 'none' }}
					variant={type === FormManagementType.all ? 'contained' : 'outlined'}
					endIcon={
						type === FormManagementType.all ? <FileUploadOutlinedIcon /> : null
					}
				>
					{type === FormManagementType.all
						? t('button.uploadForm')
						: t('button.upload')}
				</Button>
			),
		[t, userInfo]
	);
	const handleOpenUpload = (type: FormManagementType) => {
		setUploadType(type);
	};

	const handleCloseUpload = () => {
		setUploadType(null);
	};
	const formList = [
		{
			title: t('family.familyMemberApplicationFormForm'),
			type: FormManagementType.familyMember,
			setExpanded: setFamilyMemberExpanded,
			expanded: familyMemberExpanded,
			tableList: faceSheetList,
			buttonElement: <>{uploadButton(FormManagementType.familyMember)}</>,
		},
	];

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between' marginBottom={3}>
					<Typography variant='h4'>
						{t('drawerListItems.formsManagement')}
					</Typography>
				</Box>

				<Card>
					<CardContent sx={{ padding: '24px' }}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name_en')}
									value={details?.data.nameEn}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name_zh')}
									value={details?.data.nameZh}
								/>
							</Grid>
						</Grid>
						<Divider />
						<Box
							display='flex'
							justifyContent='flex-end'
							marginTop={'24px'}
							marginBottom={3}
						>
							<Button
								endIcon={<FormatLineSpacingOutlinedIcon />}
								onClick={() => {
									setFamilyMemberExpanded(!expanded);
									setExpanded(!expanded);
								}}
								sx={{ marginRight: '24px' }}
								variant='contained'
							>
								{t('button.expandCollapseAll')}
							</Button>
							{/* <UploadFormDialog /> */}
							{uploadButton(FormManagementType.all)}
							{uploadType && (
								<UploadFormDialog
									open={!!uploadType}
									onClose={handleCloseUpload}
									type={uploadType}
									userId={details?.data.userId || ''}
								/>
							)}
						</Box>
						{formList.map((item: any) => (
							<FormListContainer
								key={item.type}
								title={item.title}
								setExpanded={item.setExpanded}
								tableList={item.tableList}
								expanded={item.expanded}
								type={item.type}
								buttonElement={item.buttonElement}
							/>
						))}
					</CardContent>
				</Card>
			</Box>
		</>
	);
}
